/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細の「添付ファイル」カード。証跡（スクショ / PDF / CSV / ログ等）を append-only で表示・追加・削除する。ラスタ画像はサムネイル表示（クリックでライトボックス拡大）、それ以外は種別バッジ + ファイル名 + サイズ + ダウンロードリンク。編集可能な依頼ではドロップゾーンから追加でき（ops-core の上限で client 検証。追加の可否は API が起票者 / 担当者 / PI Div / admin で判定し、失敗はファイルごとに API のメッセージを出す）、アップロード者本人か admin は × で削除できる（所属 division は問わない）。実体は GCS、ここではメタのみを扱い配信は API プロキシ経由
 * @graph-connects OpsDetailPage [references] 依頼詳細ページ本体から添付カードを分離
 * @graph-connects useUploadOpsAttachment [calls] 添付の追加（POST multipart）を実行
 * @graph-connects useDeleteOpsAttachment [calls] 添付の削除（DELETE）を実行
 * @graph-connects uploadFilesSequentially [calls] 追加したファイルを順に送り失敗を集める
 * @graph-connects OpsFailedUploads [references] 失敗したファイルと理由の表示
 */
import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import {
  formatFileSize,
  isInlineImageMime,
  OPS_ATTACHMENT_MAX_BYTES,
  OPS_ATTACHMENT_MAX_COUNT,
} from '@cortex/ops-core';
import { useDeleteOpsAttachment, useUploadOpsAttachment } from '../../../hooks/useOps.js';
import { opsAttachmentContentUrl } from '../../../lib/api/ops.js';
import type { OpsAttachment, OpsRequest } from '../../../types/ops.js';
import { OpsAttachmentInput, type OpsAttachmentRejectReason } from './OpsAttachmentInput.js';
import {
  OpsFailedUploads,
  uploadFilesSequentially,
  type FailedUpload,
} from './OpsFailedUploads.js';

/** ファイル名から種別バッジ用の短いラベル（拡張子 大文字。無ければ FILE）を導く。 @graph-connects none */
function fileTypeLabel(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return 'FILE';
  return fileName.slice(dot + 1).toUpperCase();
}

/** 添付 1 件の行（画像はサムネイル、非画像は種別バッジ + ダウンロード）。 @graph-connects none */
function AttachmentRow({
  requestId,
  attachment,
  canRemove,
  isDeleting,
  onOpenLightbox,
  onRemove,
  t,
}: {
  requestId: string;
  attachment: OpsAttachment;
  /** この行を × で削除できるか（アップロード本人 or admin。server 認可と揃える）。 */
  canRemove: boolean;
  isDeleting: boolean;
  onOpenLightbox: (attachment: OpsAttachment) => void;
  onRemove: (attachmentId: string) => void;
  t: TFunction;
}): ReactNode {
  const contentUrl = opsAttachmentContentUrl(requestId, attachment.id);
  const isImage = isInlineImageMime(attachment.contentType);
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        {isImage ? (
          <button
            type="button"
            aria-label={t('ops.attachments.viewImage', { name: attachment.fileName })}
            onClick={() => {
              onOpenLightbox(attachment);
            }}
            className="flex items-center gap-2 rounded text-left hover:opacity-90"
          >
            <img
              src={contentUrl}
              alt={t('ops.attachments.imageAlt', { name: attachment.fileName })}
              className="h-12 w-12 shrink-0 rounded border border-[var(--color-border)] object-cover"
            />
            <span className="min-w-0">
              <span className="block truncate text-sm text-[var(--color-text-primary)]">
                {attachment.fileName}
              </span>
              <span className="block text-xs text-[var(--color-text-muted)]">
                {formatFileSize(attachment.sizeBytes)}
              </span>
            </span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <Badge variant="default" size="sm">
              {fileTypeLabel(attachment.fileName)}
            </Badge>
            <a
              href={contentUrl}
              download={attachment.fileName}
              aria-label={t('ops.attachments.download', { name: attachment.fileName })}
              className="min-w-0 truncate text-sm text-[var(--color-accent-primary)] hover:underline"
              title={attachment.fileName}
            >
              {attachment.fileName}
            </a>
            <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
              {formatFileSize(attachment.sizeBytes)}
            </span>
          </div>
        )}
      </div>
      {canRemove && (
        <button
          type="button"
          aria-label={t('ops.attachments.remove', { name: attachment.fileName })}
          disabled={isDeleting}
          onClick={() => {
            onRemove(attachment.id);
          }}
          className="shrink-0 rounded px-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-error)] disabled:opacity-50"
        >
          ×
        </button>
      )}
    </div>
  );
}

/** OpsAttachmentsCard の props。 */
export interface OpsAttachmentsCardProps {
  /** 表示対象の依頼（id / attachments を参照）。 */
  request: OpsRequest;
  /** 追加できるか（Firestore 発番 + メンバー権限）。false は閲覧のみ。 */
  editable: boolean;
  /** 現在ユーザーの nickName（削除ボタンの所有者判定用。未ログインは null）。 */
  myNickName: string | null;
  /** 現在ユーザーが admin か（所有者でなくても削除ボタンを出す。server 認可と揃える）。 */
  isAdmin: boolean;
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * 添付ファイルカード。request.attachments を一覧し、画像はサムネイル + ライトボックス、非画像はダウンロード
 * リンクで見せる。編集可能ならドロップゾーンから追加（client 検証つき）でき、各行を × で削除できる。
 * @graph-connects none
 */
export function OpsAttachmentsCard({
  request,
  editable,
  myNickName,
  isAdmin,
  t,
}: OpsAttachmentsCardProps): ReactNode {
  const uploadMutation = useUploadOpsAttachment();
  const deleteMutation = useDeleteOpsAttachment();
  const [error, setError] = useState<string | null>(null);
  const [failedUploads, setFailedUploads] = useState<FailedUpload[]>([]);
  const [lightbox, setLightbox] = useState<OpsAttachment | null>(null);

  const attachments = request.attachments;

  const handleAccept = (files: File[]): void => {
    setError(null);
    setFailedUploads([]);
    void (async () => {
      setFailedUploads(
        await uploadFilesSequentially(files, (file) =>
          uploadMutation.mutateAsync({ id: request.id, file }),
        ),
      );
    })();
  };

  const handleReject = (reason: OpsAttachmentRejectReason): void => {
    setError(`ops.attachments.errors.${reason}`);
  };

  return (
    <Card padding="sm">
      <CardHeader title={t('ops.attachments.title')} />
      <CardContent className="space-y-2 text-sm">
        {attachments.length === 0 ? (
          <p className="text-[var(--color-text-muted)]">{t('ops.attachments.empty')}</p>
        ) : (
          attachments.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              requestId={request.id}
              attachment={attachment}
              canRemove={editable && (isAdmin || attachment.uploadedBy === myNickName)}
              isDeleting={deleteMutation.isPending}
              onOpenLightbox={setLightbox}
              onRemove={(attachmentId) => {
                deleteMutation.mutate(
                  { id: request.id, attachmentId },
                  {
                    onError: () => {
                      setError('ops.attachments.errors.deleteFailed');
                    },
                  },
                );
              }}
              t={t}
            />
          ))
        )}

        {editable && (
          <div className="space-y-1.5 pt-1">
            <OpsAttachmentInput
              currentCount={attachments.length}
              onAccept={handleAccept}
              onReject={handleReject}
              t={t}
              disabled={uploadMutation.isPending}
            />
            {uploadMutation.isPending && (
              <p className="text-xs text-[var(--color-text-muted)]">
                {t('ops.attachments.uploading')}
              </p>
            )}
            {error !== null && (
              <p className="text-xs text-[var(--color-error)]">
                {t(error, {
                  count: OPS_ATTACHMENT_MAX_COUNT,
                  size: formatFileSize(OPS_ATTACHMENT_MAX_BYTES),
                })}
              </p>
            )}
            <OpsFailedUploads
              failures={failedUploads}
              title={t('ops.attachments.errors.uploadFailedCount', { count: failedUploads.length })}
              t={t}
            />
          </div>
        )}
      </CardContent>

      <Modal
        open={lightbox !== null}
        onClose={() => {
          setLightbox(null);
        }}
        title={lightbox?.fileName}
        size="full"
      >
        {lightbox !== null && (
          <img
            src={opsAttachmentContentUrl(request.id, lightbox.id)}
            alt={t('ops.attachments.imageAlt', { name: lightbox.fileName })}
            className="mx-auto max-h-[75vh] w-auto"
          />
        )}
      </Modal>
    </Card>
  );
}
